import {History, LocationDescriptor, LocationDescriptorObject, parsePath} from 'history';
import queryString from 'query-string';
import {FC, useEffect} from 'react';
import {useLocation} from 'react-router';
import LocationState = History.LocationState;

type CreateHistory<O, H> = (options?: O) => History & H;

function preserveQueryParameters(
  history: History,
  preserve: string[],
  location: LocationDescriptorObject
): LocationDescriptorObject {
  const currentQuery = queryString.parse(history.location.search);
  if (currentQuery) {
    const preservedQuery: {[key: string]: unknown} = {};
    /* eslint-disable no-restricted-syntax */
    for (const p of preserve) {
      const v = currentQuery[p];
      if (v) {
        preservedQuery[p] = v;
      }
    }
    if (location.search) {
      Object.assign(preservedQuery, queryString.parse(location.search));
    }
    // eslint-disable-next-line no-param-reassign
    location.search = queryString.stringify(preservedQuery);
  }
  return location;
}

function createLocationDescriptorObject(
  location: LocationDescriptor,
  state?: LocationState
): LocationDescriptorObject {
  return typeof location === 'string'
    ? {
        ...parsePath(location),
        state,
      }
    : location;
}

export function createPreserveQueryHistory<O, H>(
  createHistory: CreateHistory<O, H>,
  queryParameters: string[]
): CreateHistory<O, H> {
  return (options?: O) => {
    const history = createHistory(options);
    const oldPush = history.push;
    const oldReplace = history.replace;
    history.push = (path: LocationDescriptor, state?: LocationState) =>
      oldPush.apply(history, [
        preserveQueryParameters(
          history,
          queryParameters,
          createLocationDescriptorObject(path, state)
        ),
      ]);
    history.replace = (path: LocationDescriptor, state?: LocationState) =>
      oldReplace.apply(history, [
        preserveQueryParameters(
          history,
          queryParameters,
          createLocationDescriptorObject(path, state)
        ),
      ]);
    return history;
  };
}

export const ScrollToTop: FC = () => {
  const {pathname} = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

export default {createPreserveQueryHistory, ScrollToTop};
